if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('.product-media-modal__content');
        this.items = [...this.content.children].filter((item) => item.hasAttribute('data-media-id'));
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        this.zoomInButton = this.querySelector('.product-media-modal__zoom-in');
        this.zoomOutButton = this.querySelector('.product-media-modal__zoom-out');
        this.resetButton = this.querySelector('.product-media-modal__zoom-reset');
        this.zoomValue = this.querySelector('.product-media-modal__zoom-value');
        this.toolbar = this.querySelector('.product-media-modal__toolbar');
        this.lens = this.querySelector('.product-media-modal__lens');
        this.counter = this.querySelector('.product-media-modal__counter');
        this.activeIndex = 0;
        this.zoomLevel = 1;
        this.panX = 0;
        this.panY = 0;
        this.touchPointers = new Map();

        // Dawn closes a media modal on mouse pointerup. Let its controls and image handle clicks.
        [this.prevButton, this.nextButton, this.toolbar].forEach((control) => {
          control.addEventListener('pointerup', (event) => event.stopPropagation());
        });
        this.prevButton.addEventListener('click', () => this.activate(this.activeIndex - 1));
        this.nextButton.addEventListener('click', () => this.activate(this.activeIndex + 1));
        this.zoomInButton.addEventListener('click', () => this.setZoom(this.zoomLevel + 0.5));
        this.zoomOutButton.addEventListener('click', () => this.setZoom(this.zoomLevel - 0.5));
        this.resetButton.addEventListener('click', () => this.resetZoom());

        this.content.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.content.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.content.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.content.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.content.addEventListener('pointerleave', () => { this.lens.hidden = true; });
        this.content.addEventListener('click', (event) => {
          if (event.target !== this.activeImage) return;
          if (this.ignoreNextClick) {
            this.ignoreNextClick = false;
            return;
          }
          this.setZoom(this.zoomLevel > 1 ? 1 : 2);
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            this.updateLens({ clientX: event.clientX, clientY: event.clientY, pointerType: 'mouse' });
          }
        });
        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.activate(this.activeIndex - 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.activate(this.activeIndex + 1);
          }
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        const index = this.items.findIndex((item) => item.dataset.mediaId === opener.dataset.mediaId);
        this.activate(index < 0 ? 0 : index);
      }

      get activeImage() {
        const item = this.items[this.activeIndex];
        return item?.tagName === 'IMG' ? item : null;
      }

      activate(index) {
        if (!this.items.length) return;
        this.resetZoom();
        window.pauseAllMedia();
        this.activeIndex = (index + this.items.length) % this.items.length;
        this.items.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === this.activeIndex));
        const hasMultiple = this.items.length > 1;
        this.prevButton.hidden = !hasMultiple;
        this.nextButton.hidden = !hasMultiple;
        this.counter.textContent = `${this.activeIndex + 1} / ${this.items.length}`;
        this.toolbar.hidden = !hasMultiple && !this.activeImage;
        [this.zoomInButton, this.zoomOutButton, this.resetButton, this.zoomValue].forEach((control) => {
          control.hidden = !this.activeImage;
        });

        const activeMedia = this.items[this.activeIndex];
        const mediaTemplate = activeMedia.querySelector('template');
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && mediaTemplate?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }

      resetZoom() {
        this.zoomLevel = 1;
        this.panX = 0;
        this.panY = 0;
        this.touchPointers.clear();
        this.pinchStart = null;
        this.pointerStart = null;
        this.classList.remove('is-zoomed');
        this.lens.hidden = true;
        this.items.forEach((item) => {
          item.style.removeProperty('--media-zoom');
          item.style.removeProperty('--media-pan-x');
          item.style.removeProperty('--media-pan-y');
        });
        this.updateZoomControls();
      }

      setZoom(level) {
        if (!this.activeImage) return;
        this.zoomLevel = Math.max(1, Math.min(3, Math.round(level * 10) / 10));
        this.classList.toggle('is-zoomed', this.zoomLevel > 1);
        if (this.zoomLevel === 1) {
          this.panX = 0;
          this.panY = 0;
          this.lens.hidden = true;
        }
        this.activeImage.style.setProperty('--media-zoom', String(this.zoomLevel));
        this.setPan(this.panX, this.panY);
        this.updateZoomControls();
      }

      updateZoomControls() {
        this.zoomOutButton.disabled = this.zoomLevel <= 1;
        this.zoomInButton.disabled = this.zoomLevel >= 3;
        this.resetButton.disabled = this.zoomLevel <= 1;
        this.zoomValue.textContent = `${Math.round(this.zoomLevel * 100)}%`;
      }

      setPan(x, y) {
        const image = this.activeImage;
        if (!image) return;
        const maxX = Math.max(0, (image.offsetWidth * this.zoomLevel - this.content.clientWidth) / 2);
        const maxY = Math.max(0, (image.offsetHeight * this.zoomLevel - this.content.clientHeight) / 2);
        this.panX = Math.max(-maxX, Math.min(maxX, x));
        this.panY = Math.max(-maxY, Math.min(maxY, y));
        image.style.setProperty('--media-pan-x', `${this.panX}px`);
        image.style.setProperty('--media-pan-y', `${this.panY}px`);
      }

      updateLens(event) {
        if (this.zoomLevel <= 1 || !this.activeImage || event.pointerType !== 'mouse') return;
        const imageRect = this.activeImage.getBoundingClientRect();
        const dialogRect = this.querySelector('.product-media-modal__dialog').getBoundingClientRect();
        if (event.clientX < imageRect.left || event.clientX > imageRect.right ||
            event.clientY < imageRect.top || event.clientY > imageRect.bottom) {
          this.lens.hidden = true;
          return;
        }
        const size = this.lens.offsetWidth;
        const x = event.clientX - imageRect.left;
        const y = event.clientY - imageRect.top;
        this.lens.style.left = `${Math.max(0, Math.min(dialogRect.width - size, event.clientX - dialogRect.left - size / 2))}px`;
        this.lens.style.top = `${Math.max(0, Math.min(dialogRect.height - size, event.clientY - dialogRect.top - size / 2))}px`;
        this.lens.style.backgroundImage = `url("${this.activeImage.currentSrc || this.activeImage.src}")`;
        this.lens.style.backgroundSize = `${imageRect.width * this.zoomLevel}px ${imageRect.height * this.zoomLevel}px`;
        this.lens.style.backgroundPosition = `${size / 2 - x * this.zoomLevel}px ${size / 2 - y * this.zoomLevel}px`;
        this.lens.hidden = false;
      }

      onPointerDown(event) {
        if (event.target !== this.activeImage) return;
        this.ignoreNextClick = false;
        if (event.pointerType === 'touch') {
          this.touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (this.touchPointers.size === 2) {
            const [a, b] = [...this.touchPointers.values()];
            this.pinchStart = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.zoomLevel };
            this.pointerStart = null;
            return;
          }
        }
        this.pointerStart = { x: event.clientX, y: event.clientY, panX: this.panX, panY: this.panY };
      }

      onPointerMove(event) {
        if (event.pointerType === 'mouse') {
          this.updateLens(event);
          return;
        }
        if (!this.touchPointers.has(event.pointerId)) return;
        this.touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.touchPointers.size === 2 && this.pinchStart) {
          const [a, b] = [...this.touchPointers.values()];
          this.setZoom(this.pinchStart.zoom * Math.hypot(a.x - b.x, a.y - b.y) / Math.max(1, this.pinchStart.distance));
          this.ignoreNextClick = true;
        } else if (this.zoomLevel > 1 && this.pointerStart) {
          this.setPan(this.pointerStart.panX + event.clientX - this.pointerStart.x,
            this.pointerStart.panY + event.clientY - this.pointerStart.y);
        }
      }

      onPointerUp(event) {
        if (event.target === this.activeImage || this.pointerStart || this.touchPointers.has(event.pointerId)) {
          event.stopPropagation();
        }
        this.touchPointers.delete(event.pointerId);
        if (this.touchPointers.size < 2) this.pinchStart = null;
        if (!this.pointerStart) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        if (event.pointerType === 'touch' && this.zoomLevel === 1 &&
            Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.ignoreNextClick = true;
          this.activate(this.activeIndex + (deltaX < 0 ? 1 : -1));
        } else if (Math.abs(deltaX) + Math.abs(deltaY) > 8) {
          this.ignoreNextClick = true;
        }
        this.pointerStart = null;
      }
    }
  );
}
